import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import {
	addressString,
	convertLLCAddressToCloserAddress,
	getLLCAddressString,
} from '@core/util/address';
import {
	PrefilledNewClient,
	getLLCClientName,
	llcEstateTypeToCloserType,
} from '@core/util/llc';
import { dollarsToCents } from '@core/util/money';
import { Address } from '@models/address';
import { DATE_FORMAT } from '@models/businesses';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
	SpousalStatus,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	ClientAddressOveride,
	LlcRepoService,
	SyncMortageDetails,
} from '@repos/mortgages/llc.service';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { LLCMortgageState } from '@state/mortgages/llc-mortgage/llc-mortgage.state';
import { format, parseISO } from 'date-fns';
import LogRocket from 'logrocket';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { MortgageInstructionPerson } from '@models/mortgages/mortgage-base';

interface ClientAddressVM {
	llcClient: MortgageInstructionPerson;
	closerClient: PersonMatterClient | BusinessMatterClient;
	overrideAddress: boolean;
	model: string;
	llcAddressString: string;
	llcClientName: string;
	closerAddressString: string;
	closerClientName: string;
	llcSpouseName: string;
	closerSpouse: PersonClient | BusinessClient;
}
@UntilDestroy()
@Component({
	selector: 'app-llc-sync-data',
	templateUrl: './sync-data.component.html',
	styleUrls: ['./sync-data.component.scss'],
	standalone: false,
})
export class SyncDataComponent implements OnInit {
	public ICON = ICON;
	public loading = false;
	public primaryTitle: MatterTitle;

	public overridePurchasePrice = false;
	public overrideClosingDate = false;
	public overridePropertyAddress = false;
	public overrideLegalDescription = false;
	public overrideEstateType = false;
	public llcMortgage: LLCTransaction;
	public clientsVM: ClientAddressVM[] = [];

	public closingDate;
	public address;
	public legalDescription;
	public purchasePrice;
	public estateType;

	public matterPurchasePrice;
	public matterClosingDate;
	public matterAddress;
	public matterLegalDescription;
	public matterEstateType;

	public llcPurchasePrice;
	public llcClosingDate;
	public llcAddress;
	public llcAddressString;
	public llcLegalDescription;

	public primaryLegalDescriptionId: string;

	public llcEstateType;

	@Select(SelectedMatter.purchasePrice) purchasePrice$: Observable<string>;
	@Select(SelectedMatter.closingDate) closingDate$: Observable<string>;
	@Select(SelectedMatter.address) address$: Observable<Address>;
	@Select(SelectedMatter.interestEstate) estateType$: Observable<string>;
	@Select(MatterTitlesState.getPrimaryTitle)
	primaryTitle$: Observable<MatterTitle>;

	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	clients$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	@Select(LLCMortgageState.getLLCMortgage) llcMortgageFilterFn$: Observable<
		(id: string) => LLCTransaction
	>;

	@SelectSnapshot(SelectedMatter.id) matterId: string;

	@Input() showPriceOveride = false;
	@Input() transaction: LLCTransaction;
	@Output() next: EventEmitter<void> = new EventEmitter();

	constructor(
		private llcRepo: LlcRepoService,
		private snackbarService: SnackbarService,
	) {}

	async _next() {
		try {
			this.loading = true;

			// call llc service to sync the data
			const syncData: SyncMortageDetails = {
				salePrice: this.overridePurchasePrice ? this.llcPurchasePrice : null,
				closingDate: this.overrideClosingDate ? this.llcClosingDate : null,
				propertyAddress: this.overridePropertyAddress
					? convertLLCAddressToCloserAddress(this.llcAddress)
					: null,
				primaryLegalDescriptionId: this.primaryLegalDescriptionId,
				legalDescription: this.overrideLegalDescription
					? this.llcLegalDescription
					: null,
				estateType:
					this.overrideEstateType && this.llcEstateType
						? llcEstateTypeToCloserType(this.llcEstateType)
						: null,
				clientServiceAddress: this.clientsVM.reduce((acc, curr) => {
					if (curr.overrideAddress) {
						const addressOverride: ClientAddressOveride = {
							id: curr.closerClient.id,
							address: convertLLCAddressToCloserAddress(
								curr.llcClient?.address,
							),
						};
						acc.push(addressOverride);
					}

					return acc as ClientAddressOveride[];
				}, []),
			};

			await this.llcRepo.syncMortgageDataToMatterAndClients(
				this.matterId,
				syncData,
			);
			this.next.emit();
		} catch (err) {
			console.log('ERROR', err);
			this.snackbarService.openSnackBar({
				text: 'Unable to sync data from mortgage to matter.',
				type: 'error',
			});
			LogRocket.log('Unable to sync llc mortgage data to matter', err);
		} finally {
			this.loading = false;
		}
	}

	ngOnInit(): void {
		this.purchasePrice$.pipe(take(1)).subscribe((_purchasePrice) => {
			this.purchasePrice = _purchasePrice;
			this.matterPurchasePrice = _purchasePrice;
		});

		this.closingDate$.pipe(take(1)).subscribe((_closingDate) => {
			this.closingDate = _closingDate;
			this.matterClosingDate = _closingDate;
		});

		this.address$.pipe(take(1)).subscribe((_address) => {
			this.address = addressString(_address);
			this.matterAddress = addressString(_address);
		});

		this.primaryTitle$.pipe(take(1)).subscribe((primaryTitle) => {
			if (primaryTitle) {
				this.primaryLegalDescriptionId = primaryTitle.id;
				this.primaryTitle = primaryTitle;
				this.legalDescription = primaryTitle.legalDescription;
				this.matterLegalDescription = primaryTitle.legalDescription;
			}
		});

		this.estateType$.pipe(take(1)).subscribe((_estateType) => {
			this.estateType = _estateType;
			this.matterEstateType = _estateType;
		});

		combineLatest([this.clients$, this.getLLcMortgage$(this.transaction.id)])
			.pipe(take(1))
			.subscribe(([clients, llcMortgage]) => {
				this.llcMortgage = llcMortgage;
				if (llcMortgage) {
					this.llcPurchasePrice = dollarsToCents(
						llcMortgage?.data?.mortgage?.purchasePrice?.toString(),
					);
					this.llcClosingDate = llcMortgage?.overview?.closingDate
						? format(parseISO(llcMortgage?.overview?.closingDate), DATE_FORMAT)
						: '';
					this.llcLegalDescription =
						llcMortgage?.data?.properties?.length > 0
							? llcMortgage?.data?.properties[0].legalDescription
							: '';
					this.llcAddress =
						llcMortgage?.data?.properties?.length > 0
							? llcMortgage?.data?.properties[0]?.address
							: null;
					this.llcAddressString = this.llcAddress
						? getLLCAddressString(this.llcAddress)
						: null;
					if (llcMortgage?.data?.properties[0]) {
						if (
							llcMortgage?.data?.properties[0]?.estateType?.toLowerCase() ===
							'other'
						) {
							this.llcEstateType =
								llcMortgage.data.properties[0]?.otherEstateTypeDescription ??
								'';
						} else {
							this.llcEstateType = llcMortgage.data.properties[0].estateType;
						}
					}
				}
				const _clientsVM: ClientAddressVM[] = [];
				const mappings = llcMortgage?.clientMappings;

				llcMortgage?.data?.mortgagors.forEach((mortgagor) => {
					const closerClientIdMappings = mappings
						.filter((mapping) => mapping.llcId === mortgagor.id)
						.map((mapping) => mapping.closerClientId);

					closerClientIdMappings?.forEach((closerClientId) => {
						const client = clients.find((x) => x.id === closerClientId);
						let spouse;
						if (
							(client as PersonMatterClientWithAssociations)?.flaClient &&
							client?.fla?.spousalStatus === SpousalStatus.CONSENTING
						) {
							spouse = (client as PersonMatterClientWithAssociations)
								?.flaClient;
						}

						if (client) {
							_clientsVM.push({
								llcClient: mortgagor,
								closerClient: client,
								overrideAddress: false,
								model: addressString(client.serviceAddress),
								llcAddressString: getLLCAddressString(mortgagor.address),
								closerAddressString: addressString(client.serviceAddress),
								llcClientName: getLLCClientName(mortgagor),
								closerClientName: this.getCloserClientName(client),
								llcSpouseName: mortgagor?.spouseFirstName
									? `${mortgagor?.spouseFirstName ?? ''} ${mortgagor?.spouseMiddleName ?? ''} ${mortgagor?.spouseLastName ?? ''}`
									: null,
								closerSpouse: spouse,
							});
						}
					});
				});

				this.clientsVM = _clientsVM;
			});
	}

	getPrefilledNewClient(
		clientType: ClientType,
		llcPerson: MortgageInstructionPerson,
	): PrefilledNewClient {
		return {
			firstName: llcPerson?.firstName,
			middleNames: llcPerson?.middleName,
			lastName: llcPerson?.lastName,
			type: clientType,
			name: llcPerson?.companyName,
			phone: llcPerson?.phone,
			workPhone: llcPerson?.businessPhone,
		};
	}

	getClientType = (type: string): ClientType =>
		type?.toLowerCase() === 'person'
			? ClientType.PERSON
			: ClientType.CORPORATION;

	getCloserClientName = (client: PersonClient | BusinessClient) =>
		client.type === ClientType.PERSON
			? `${(client as PersonClient).firstName} ${(client as PersonClient).lastName}`
			: (client as BusinessClient).name;

	getLLcMortgage$(id: string) {
		return this.llcMortgageFilterFn$.pipe(
			untilDestroyed(this),
			map((fn) => fn(id)),
		);
	}

	updateLegalDescription($event) {
		this.overrideLegalDescription = $event;
		this.legalDescription = $event
			? this.llcLegalDescription
			: this.matterLegalDescription;
	}

	updateEstateType($event) {
		this.overrideEstateType = $event;
		this.estateType = $event ? this.llcEstateType : this.matterEstateType;
	}

	updatePurchasePrice($event) {
		this.overridePurchasePrice = $event;
		this.purchasePrice = $event
			? this.llcPurchasePrice
			: this.matterPurchasePrice;
	}

	updateClosingDate($event) {
		this.overrideClosingDate = $event;
		this.closingDate = $event ? this.llcClosingDate : this.matterClosingDate;
	}

	updateAddress($event) {
		this.overridePropertyAddress = $event;
		this.address = $event ? this.llcAddressString : this.matterAddress;
	}

	updateClientAddress(client: ClientAddressVM, $event: boolean) {
		client.overrideAddress = $event;
		client.model = $event
			? client.llcAddressString
			: client.closerAddressString;
	}
}
